import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {navigate} from 'gatsby';
import * as yup from 'yup';

import Button from '@src/components/Button';
import {Formik, FormikProps, Form, Field} from 'formik';
import {getLanguageByCode} from '@src/i18n/utils';
import {UntranslatedTextInput} from '@src/components/Form/TextInput';
import {UntranslatedRadioOptions} from '@src/components/Form/RadioOptions';
import {TFunction} from 'i18next';

import * as styles from './leadGenForm.module.scss';

import type {Languages} from '@src/i18n';

// Translate Form components for use with this form.
const TextInput = withTranslation('LeadGenForm')(UntranslatedTextInput);
const RadioOptions = withTranslation('LeadGenForm')(UntranslatedRadioOptions);

interface LeadGenFormProps extends WithTranslation<'LeadGenForm'> {
  languages: Languages[];
  leadGenEndpoint: string;
  webhookAuth: string;
  heading?: string;
  text?: string;
  submitButtonLabel?: string;
}

interface LeadGenFormValues {
  firstName: string;
  language: string;
  lastName: string;
  phone: string;
  zipCode: string;
}

const getInitialValues = (language: string) => ({
  firstName: '',
  language,
  lastName: '',
  phone: '',
  zipCode: '',
});

const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    firstName: yup.string().required(t('firstNameError')),
    lastName: yup.string().required(t('lastNameError')),
    phone: yup
      .string()
      .matches(
        new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
        t('phoneError'),
      )
      .required(t('phoneError')),
    zipCode: yup
      .string()
      .matches(RegExp(/^\d{5}$/), t('zipCodeError'))
      .required(t('zipCodeError')),
  });

class LeadGenForm extends React.Component<LeadGenFormProps> {
  handleSubmit = (
    values: LeadGenFormValues,
    {setSubmitting}: FormikProps<LeadGenFormValues>,
  ) => {
    const {t, leadGenEndpoint, webhookAuth} = this.props;

    fetch(leadGenEndpoint, {
      body: JSON.stringify(values),
      headers: {
        Accept: 'application/json',
        Authorization: 'Basic ' + webhookAuth,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(res => {
        if (res.status === 200) {
          setSubmitting(false);
          navigate(t('confirmationUrl'));
        } else {
          throw new Error();
        }
      })
      .catch(e => {
        setSubmitting(false);
        navigate(t('errorUrl'));
      });
  };

  render() {
    const {
      languages,
      leadGenEndpoint,
      t,
      i18n,
      heading,
      text,
      submitButtonLabel,
    } = this.props;
    const language = getLanguageByCode(i18n.language, languages);
    const initialValues = getInitialValues(language?.name);
    const validationSchema = getValidationSchema(t);
    const defaultText = `
      <p class="${styles.text}">${t('text')}</p>
      <p class="${styles.text}">${t('subtext')}</p>
   `;

    return (
      <div>
        <div className={styles.wrapper} id={t('link')}>
          <hr className={styles.bar} />
          <div className={styles.content}>
            <h2 className={styles.heading}>{heading || t('heading')}</h2>
            <div className={styles.row}>
              <div
                className={`${styles.left} ${styles.text}`}
                dangerouslySetInnerHTML={{__html: text || defaultText}}
              />
              <div className={styles.right}>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={this.handleSubmit}>
                  {({isSubmitting}) => (
                    <Form action={leadGenEndpoint} method="POST">
                      <div className={styles.formGroup}>
                        <TextInput
                          name="firstName"
                          required={false}
                          inlineWidth="formColHalf"
                          inverse={true}
                        />
                        <TextInput
                          name="lastName"
                          required={false}
                          inlineWidth="formColHalf"
                          inverse={true}
                        />
                      </div>

                      <TextInput name="phone" required={false} inverse={true} />
                      <TextInput
                        name="zipCode"
                        required={false}
                        inverse={true}
                      />

                      <RadioOptions
                        name="language"
                        options={['English', 'Spanish']}
                        required={false}
                        display="inline"
                        inverse={true}
                      />

                      <span className={styles.submit}>
                        <Button
                          type="submit"
                          theme="secondary"
                          size="large"
                          text={submitButtonLabel || t('submitButtonText')}
                          disabled={isSubmitting}
                        />
                        <p className={styles.legal}>{t('legalText')}</p>
                      </span>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation('LeadGenForm')(LeadGenForm);
